import { FC, useCallback, useEffect, useState } from 'react'
import { cn } from '@bem-react/classname'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'

import { Memo } from '@/hoc/Memo'
import yup from '@/utils/yup-extended'
import useBackendError from '@/hooks/useBackendError'
import { useAppDispatch } from '@/core/store/hooks'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { ToastType } from '@/core/services/Toast/types/ToastType'
import { CustomerModel } from '../../models/LicensesModel'
import { updateLicenseThunk } from '../../store/licenses.slice'
import { Select } from '@/components/form/Select'

import { FormGroup } from '@/components/form/FormGroup'
import { Button } from '@/components/Button'
import { Field } from '@/components/form/Field'
import countryList from 'react-select-country-list'

import './CustomerModal.scss'
import { CountryDataModel } from '@/modules/AccountPage/models/UserModel'
import { changeLoading } from '@/core/services/Loading/store/loading.slice'

const cnCustomerModal = cn('CustomerModal')

const nameRegexCustomer = /^[A-Za-z0-9\s]+$/

interface CustomerModalInterface {
  customer?: (CustomerModel & { id: string }) | null
  onClose: () => void
  onSetCustomer?: (customer: CustomerModel) => void
}

export const CustomerModal: FC<CustomerModalInterface> = Memo(
  ({ customer, onClose, onSetCustomer }: CustomerModalInterface) => {
    const { t, i18n, ready } = useTranslation('translation')
    const dispatch = useAppDispatch()
    const [setBackendError, fieldsError] = useBackendError()
    const [countries, setCountries] = useState<CountryDataModel[]>([])

    const CustomerSchema = yup.object().shape({
      customerName: yup
        .string()
        .trim()
        .nullable()
        .required(t('This field is required'))
        .matches(
          nameRegexCustomer,
          t(
            'Only English letters and numbers. Special characters (except spaces) not allowed',
          ),
        )
        .min(
          1,
          t('The value must be at least {{value}} characters long', {
            value: 1,
          }),
        ),
      customerSurname: yup
        .string()
        .trim()
        .nullable()
        .required(t('This field is required'))
        .matches(
          nameRegexCustomer,
          t(
            'Only English letters and numbers. Special characters (except spaces) not allowed',
          ),
        )
        .min(
          1,
          t('The value must be at least {{value}} characters long', {
            value: 1,
          }),
        ),
      customerFarm: yup
        .string()
        .trim()
        .nullable()
        .required(t('This field is required'))
        .matches(
          nameRegexCustomer,
          'Only English letters and numbers. Special characters (except spaces) not allowed',
        )
        .min(
          4,
          t('The value must be at least {{value}} characters long', {
            value: 4,
          }),
        )
        .max(
          40,
          t('The value must contain no more than {{value}} characters.', {
            value: 40,
          }),
        ),
      customerPhone: yup
        .string()
        .trim()
        .when({
          is: (val: string) => val && val.length > 0,
          then: yup.string().trim().isValidPhone(t('isValidPhone')),
        })
        .nullable()
        .required(t('This field is required')),
      customerAddress: yup.string().trim().nullable(),
      customerEmail: yup
        .string()
        .trim()
        .email(t('Email is not correct'))
        .required(t('This field is required')),
      countryCode: yup
        .string()
        .typeError(t('This field is required'))
        .required(t('This field is required')),
      note: yup
        .string()
        .trim()
        .nullable()
        .max(
          150,
          t('The value must contain no more than {{value}} characters.', {
            value: 150,
          }),
        ),
    })

    const {
      reset,
      register,
      control,
      handleSubmit,
      formState: { errors, isSubmitting },
    } = useForm<CustomerModel & { id: string }>({
      mode: 'onChange',
      resolver: yupResolver(CustomerSchema),
    })

    useEffect(() => {
      const list = countryList().getData()
      const upd = list.map(el => {
        return {
          ...el,
          label: t(`countries:${el.value}`),
        }
      })
      if (i18n.language === 'en-GB') setCountries(list)
      else setCountries(upd)
    }, [i18n, ready])

    useEffect(() => {
      if (customer) reset(customer)
    }, [customer])

    const updateCustomer = useCallback(
      async (data: CustomerModel & { id: string }) => {
        try {
          const res = await dispatch(updateLicenseThunk(data)).unwrap()
          if (res) {
            dispatch(
              setToast({
                type: ToastType.success,
                message: t('Customer data successfully updated'),
              }),
            )
            onClose()
          }
        } catch (errors) {
          setBackendError(errors)
          dispatch(changeLoading(false))
        }
      },
      [],
    )

    const onSubmit = async (data: CustomerModel & { id: string }) => {
      if (customer) {
        dispatch(changeLoading(true))
        await updateCustomer(data)
      } else dispatch(changeLoading(true))
      onSetCustomer && onSetCustomer(data as CustomerModel)
    }

    return (
      <form className={cnCustomerModal()} onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Field
            {...register('customerName')}
            placeholder={t('Customer Name')}
            label={t('Customer Name')}
            required
            errors={
              (errors.customerName && errors.customerName.message) ||
              (fieldsError &&
                fieldsError.customerName &&
                fieldsError.customerName.message)
            }
          />
        </FormGroup>
        <FormGroup>
          <Field
            {...register('customerSurname')}
            placeholder={t('Customer Surname')}
            label={t('Customer Surname')}
            required
            errors={
              (errors.customerSurname && errors.customerSurname.message) ||
              (fieldsError &&
                fieldsError.customerSurname &&
                fieldsError.customerSurname.message)
            }
          />
        </FormGroup>
        <FormGroup>
          <Field
            {...register('customerFarm')}
            placeholder={t('Farm Name')}
            label={t('Farm Name')}
            required
            errors={
              (errors.customerFarm && errors.customerFarm.message) ||
              (fieldsError &&
                fieldsError.customerFarm &&
                fieldsError.customerFarm.message)
            }
          />
        </FormGroup>
        <FormGroup>
          <Select
            label={t('Customer Country')}
            placeholder={t('(None)')}
            control={control}
            name='countryCode'
            options={countries}
            optionLabel='label'
            optionValue='value'
            required
            errors={
              (errors.countryCode && errors.countryCode.message) ||
              (fieldsError &&
                fieldsError.countryCode &&
                fieldsError.countryCode.message)
            }
            isNone
          />
        </FormGroup>
        <FormGroup>
          <Field
            {...register('customerEmail')}
            placeholder={t('Customer Email')}
            label={t('Customer Email')}
            required
            errors={
              (errors.customerEmail && errors.customerEmail.message) ||
              (fieldsError &&
                fieldsError.customerEmail &&
                fieldsError.customerEmail.message)
            }
          />
        </FormGroup>
        <FormGroup>
          <Field
            {...register('customerPhone')}
            placeholder={t('Customer Phone')}
            label={t('Customer Phone')}
            required
            errors={
              (errors.customerPhone && errors.customerPhone.message) ||
              (fieldsError &&
                fieldsError.customerPhone &&
                fieldsError.customerPhone.message)
            }
          />
        </FormGroup>
        <FormGroup>
          <Field
            {...register('customerAddress')}
            placeholder={t('Customer Address')}
            label={t('Customer Address')}
            errors={
              (errors.customerAddress && errors.customerAddress.message) ||
              (fieldsError &&
                fieldsError.customerAddress &&
                fieldsError.customerAddress.message)
            }
          />
        </FormGroup>

        <FormGroup>
          <Field
            {...register('note')}
            placeholder={t('Note')}
            label={t('Note')}
            errors={
              (errors.note && errors.note.message) ||
              (fieldsError && fieldsError.note && fieldsError.note.message)
            }
          />
        </FormGroup>

        <div className={cnCustomerModal('btn')}>
          <Button large type='submit' isLoading={isSubmitting}>
            {t('Submit')}
          </Button>
        </div>
      </form>
    )
  },
)
